import React from 'react';
import BaseResultDisplay from '../common/BaseResultDisplay';
import { Card, Typography, Tag, Row, Col, Statistic, Divider, Progress, Space } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { ResultComponentProps } from '../common/types';
import BatchSequenceResult from './BatchSequenceResult';

const { Text, Title, Paragraph } = Typography;

// 序列验证结果组件
const SequenceValidationResult: React.FC<ResultComponentProps> = ({
  data,
  title,
  onDeleteResult,
  multiResults = false,
  resultIndex,
}) => {
  // 检查是否为批量结果 - 支持新旧格式
  const isBatchResult = (data?.items && Array.isArray(data.items) && data.count !== undefined) || 
                       (data?.results && Array.isArray(data.results) && data.total_sequences !== undefined);
  
  // 如果是批量结果，使用批量组件
  if (isBatchResult) {
    return (
      <BatchSequenceResult
        data={data}
        title={title || '批量序列验证结果'}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        resultIndex={resultIndex}
      />
    );
  }

  // 原有的单序列验证逻辑保持不变
  // 提取验证结果数据
  const isValid = data?.is_valid;
  const detectedType = data?.detected_type?.toUpperCase() || '';
  const message = data?.message || '';
  const filteredSequence = data?.filtered_sequence || '';
  const originalId = data?.originalId || '';
  const statistics = data?.statistics || {};
  
  // 计算核苷酸组成百分比
  const calculatePercentage = (count: number, total: number) => {
    return total > 0 ? Math.round((count / total) * 100) : 0;
  };
  
  // 提取序列统计信息
  const length = statistics.length || 0;
  const aCount = statistics.A || 0;
  const tCount = statistics.T || 0;
  const gCount = statistics.G || 0;
  const cCount = statistics.C || 0;
  const nCount = statistics.N || 0;
  const gcContent = statistics.gc_content || 0;
  
  // 计算各核苷酸百分比
  const aPercentage = calculatePercentage(aCount, length);
  const tPercentage = calculatePercentage(tCount, length);
  const gPercentage = calculatePercentage(gCount, length);
  const cPercentage = calculatePercentage(cCount, length);
  const nPercentage = calculatePercentage(nCount, length);
  
  // 获取验证状态标签
  const getStatusTag = () => {
    if (isValid) {
      return <Tag color="success" icon={<CheckCircleOutlined />}>有效序列</Tag>;
    } else if (filteredSequence) {
      return <Tag color="warning" icon={<WarningOutlined />}>包含无效字符</Tag>;
    } else {
      return <Tag color="error" icon={<CloseCircleOutlined />}>无效序列</Tag>;
    }
  };
  
  // 核苷酸颜色映射
  const nucleotideColors = {
    'A': '#A0A0FF', // 蓝色
    'T': '#FF8080', // 红色
    'G': '#FFD700', // 金色
    'C': '#80FF80', // 绿色
    'N': '#CCCCCC', // 灰色
  };
  
  // 渲染核苷酸组成条形图
  const renderNucleotideComposition = () => {
    // 确保GC含量正确计算为百分比
    const gcPercentage = gcContent ? Math.round(gcContent) : 0;
    
    return (
      <div style={{ marginTop: 16 }}>
        <Title level={5}>核苷酸组成</Title>
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', height: 20, width: '100%', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${aPercentage}%`, backgroundColor: nucleotideColors.A }} />
            <div style={{ width: `${tPercentage}%`, backgroundColor: nucleotideColors.T }} />
            <div style={{ width: `${gPercentage}%`, backgroundColor: nucleotideColors.G }} />
            <div style={{ width: `${cPercentage}%`, backgroundColor: nucleotideColors.C }} />
            <div style={{ width: `${nPercentage}%`, backgroundColor: nucleotideColors.N }} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Space>
            <Tag color={nucleotideColors.A} style={{ color: '#000' }}>A: {aPercentage}%</Tag>
            <Tag color={nucleotideColors.T} style={{ color: '#000' }}>T: {tPercentage}%</Tag>
            <Tag color={nucleotideColors.G} style={{ color: '#000' }}>G: {gPercentage}%</Tag>
            <Tag color={nucleotideColors.C} style={{ color: '#000' }}>C: {cPercentage}%</Tag>
            {nCount > 0 && <Tag color={nucleotideColors.N} style={{ color: '#000' }}>N: {nPercentage}%</Tag>}
          </Space>
          <Tag color="#108ee9">GC含量: {gcPercentage}%</Tag>
        </div>
      </div>
    );
  };
  
  // 渲染序列信息
  const renderSequenceInfo = () => {
    // 确保GC含量正确计算为百分比
    const gcPercentage = gcContent ? Math.round(gcContent) : 0;
    
    return (
      <div style={{ marginTop: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Card size="small">
              <Statistic 
                title="序列长度" 
                value={length} 
                suffix="bp" 
                valueStyle={{ color: '#3f8600' }} 
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic 
                title="序列类型" 
                value={detectedType} 
                valueStyle={{ color: '#1890ff' }} 
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic 
                title="GC含量" 
                value={gcPercentage} 
                suffix="%" 
                valueStyle={{ color: gcContent > 0.6 ? '#cf1322' : gcContent < 0.4 ? '#faad14' : '#3f8600' }}
                groupSeparator=""
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  };
  
  // 渲染序列预览
  const renderSequencePreview = () => {
    if (!filteredSequence) return null;
    
    // 只显示前50个碱基，后面用...表示
    const displaySequence = filteredSequence.length > 50 
      ? filteredSequence.substring(0, 50) + '...' 
      : filteredSequence;
    
    return (
      <div style={{ marginTop: 16 }}>
        <Title level={5}>序列预览</Title>
        <div style={{ 
          fontFamily: 'monospace', 
          padding: 8, 
          backgroundColor: '#f5f5f5', 
          borderRadius: 4,
          overflowX: 'auto',
          whiteSpace: 'pre'
        }}>
          {displaySequence}
        </div>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">显示前50个碱基，总长度: {filteredSequence.length} bp</Text>
        </div>
      </div>
    );
  };
  
  return (
    <BaseResultDisplay
      title={title || "序列统计结果"}
      data={data}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      showRawData={false}
      showDistribution={false}
    >
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            {getStatusTag()}
            <Text strong>{originalId || '序列'}</Text>
          </Space>
        </div>
        
        <Paragraph style={{ marginTop: 16 }}>
          {message}
        </Paragraph>
        
        <Divider style={{ margin: '16px 0' }} />
        
        {renderSequenceInfo()}
        
        {renderNucleotideComposition()}
        
        {renderSequencePreview()}
      </div>
    </BaseResultDisplay>
  );
};

export default SequenceValidationResult;